import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import RefreshIcon from '@mui/icons-material/Refresh';
import { API_BASE_URL } from '../../config/config.js';

// Grafana is proxied at /grafana/ on the same domain as the admin page (fids.eximenon.com).
// Dashboard UIDs are provisioned by the install script; they match the UIDs in
// config/grafana-provisioning/dashboards/.
const GRAFANA_BASE = '/grafana';

const DASHBOARDS = [
    {
        id: 'system',
        label: 'System',
        uid: 'rYdddlPWk',
        title: 'Node Exporter Full — System Overview',
        description: 'CPU, memory, disk I/O, network — from node_exporter on both servers',
    },
    {
        id: 'app',
        label: 'Application',
        uid: 'fids-app',
        title: 'FIDS Application Metrics',
        description: 'HTTP latency P95/P99, request rate, error rate, Node.js heap',
    },
    {
        id: 'mongodb',
        label: 'Database',
        uid: '6Lk9wMHik',
        title: 'MongoDB Overview',
        description: 'Query latency, connections, operations/sec, slow queries',
    },
    {
        id: 'logs',
        label: 'Logs',
        uid: 'sadlil-loki-apps-dashboard',
        title: 'Logs / App',
        description: 'Log volume by level, error spikes, log explosion detection via Loki',
    },
    {
        id: 'health',
        label: 'Stack Health',
        uid: 'fids-obs-health',
        title: 'Observability Stack Health',
        description: 'Prometheus scrape health, all targets up/down, Loki ingestion, cardinality',
    },
];

function buildGrafanaUrl(uid) {
    return `${GRAFANA_BASE}/d/${uid}?orgId=1&kiosk=tv&refresh=30s`;
}

export default function ObservabilityPanel() {
    const [tab, setTab] = useState(0);
    const [authState, setAuthState] = useState('idle'); // idle | loading | ready | error
    const [authError, setAuthError] = useState('');

    const establishGrafanaSession = useCallback(async () => {
        setAuthState('loading');
        setAuthError('');
        try {
            const authData = localStorage.getItem('auth');
            if (!authData) throw new Error('Not authenticated');
            const { token } = JSON.parse(authData);

            // This endpoint lives on fids.eximenon.com (proxied via nginx-frontend.conf),
            // NOT on api.fids.eximenon.com, so the resulting Set-Cookie applies to
            // fids.eximenon.com and will be sent with /grafana/* iframe requests.
            const res = await fetch(`${window.location.origin}/grafana-auth-set`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                credentials: 'include',
            });

            if (!res.ok) throw new Error(`Auth setup failed (${res.status})`);
            setAuthState('ready');
        } catch (err) {
            setAuthError(err.message || 'Failed to connect to observability stack');
            setAuthState('error');
        }
    }, []);

    useEffect(() => {
        establishGrafanaSession();
    }, [establishGrafanaSession]);

    const current = DASHBOARDS[tab];

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a2e', mb: 0.5 }}>
                    Observability
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Real-time metrics for both production servers — powered by Prometheus &amp; Grafana
                </Typography>
            </Box>

            {authState === 'loading' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="text.secondary">
                        Connecting to observability stack…
                    </Typography>
                </Box>
            )}

            {authState === 'error' && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={establishGrafanaSession}
                        >
                            Retry
                        </Button>
                    }
                >
                    {authError}
                    {' — '}
                    Ensure the observability stack is installed on the server
                    (run <code>scripts/install-observability.sh</code>).
                </Alert>
            )}

            {authState === 'ready' && (
                <>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}>
                        <Tabs
                            value={tab}
                            onChange={(_, v) => setTab(v)}
                            aria-label="observability dashboards"
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            {DASHBOARDS.map((d, i) => (
                                <Tab key={d.id} label={d.label} id={`obs-tab-${i}`} aria-controls={`obs-panel-${i}`} />
                            ))}
                        </Tabs>
                    </Box>

                    {DASHBOARDS.map((d, i) => (
                        <Box
                            key={d.id}
                            role="tabpanel"
                            hidden={tab !== i}
                            id={`obs-panel-${i}`}
                            aria-labelledby={`obs-tab-${i}`}
                        >
                            {tab === i && (
                                <Box>
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        py: 1,
                                        px: 0.5,
                                    }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {d.description}
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            href={buildGrafanaUrl(d.uid)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ ml: 2, flexShrink: 0 }}
                                        >
                                            Open in Grafana
                                        </Button>
                                    </Box>
                                    <Box
                                        component="iframe"
                                        src={buildGrafanaUrl(d.uid)}
                                        title={d.title}
                                        sx={{
                                            width: '100%',
                                            height: 'calc(100vh - 280px)',
                                            minHeight: 500,
                                            border: '1px solid #e0e0e0',
                                            borderRadius: 1,
                                            display: 'block',
                                        }}
                                        allow="fullscreen"
                                    />
                                </Box>
                            )}
                        </Box>
                    ))}
                </>
            )}
        </Box>
    );
}
